import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/hooks/useAuth';

const supportTypesOptions = [
  'Fiscal Sponsorship',
  'Fundraising',
  'Development Funds',
  'Production Resources',
  'Post-production Resources',
  'Impact Campaign Resources',
  'Marketing Resources',
  'Distribution Support'
];

const distributionGoalsOptions = [
  'Wide Audience Reach',
  'Targeted Niche Markets',
  'International Exposure',
  'Festival Acclaim',
  'Educational Impact',
  'Critical Acclaim and Awards',
  'Direct-to-Consumer Sales',
  'Partnerships and Sponsorships'
];

const contentFormatsOptions = [
  'Documentary Feature',
  'Documentary Series',
  'Documentary Short',
  'Narrative Feature',
  'Narrative Series',
  'Narrative Short',
  'Social Media Shorts',
  'Podcast'
];

const teamQualitiesOptions = [
  'Diverse Representation',
  'Industry Experience',
  'Subject Matter Expertise',
  'Impact Track Record',
  'Creative Vision',
  'Technical Skills',
  'Project Management',
  'Community Connections'
];

const festivalImportanceOptions = [
  'Essential',
  'Very Important',
  'Somewhat Important',
  'Not Important'
];

const streamerImportanceOptions = [
  'Essential',
  'Very Important',
  'Somewhat Important',
  'Not Important'
];

const similarWorkImportanceOptions = [
  'Required',
  'Preferred',
  'Not Required'
];

const firstTimeFilmmakerOptions = [
  'Welcome and Supported',
  'Considered with Strong Concept',
  'Must Have Co-Production',
  'Not Accepting'
];

const studioRolesOptions = [
  'Executive Producer',
  'Co-Producer',
  'Development Partner',
  'Distribution Partner',
  'Impact Partner',
  'Marketing Partner'
];

const creativeControlOptions = [
  'Full Creative Freedom',
  'Collaborative Process',
  'Studio Has Final Say',
  'Project Dependent'
];

const fundingAspectsOptions = [
  'Full Funding',
  'Partial Funding',
  'Gap Financing',
  'Matching Funds',
  'Grant Support',
  'Crowdfunding Support'
];

const projectReturnsOptions = [
  'Revenue Share',
  'Profit Share',
  'Equity Stake',
  'Distribution Rights',
  'Impact Metrics',
  'Festival Success'
];

const criteriaSchema = z.object({
  studioName: z.string().min(1, 'Studio name is required'),
  description: z.string().min(1, 'Description is required'),
  coreValues: z.string().min(1, 'Core values are required'),
  socialIssues: z.string().min(1, 'Social issues are required'),
  successMetrics: z.string().min(1, 'Success metrics are required'),
  supportTypes: z.array(z.string()).min(1, 'Select at least one support type'),
  distributionGoals: z.array(z.string()).min(1, 'Select at least one distribution goal'),
  contentFormats: z.array(z.string()).min(1, 'Select at least one content format'),
  teamQualities: z.array(z.string()).min(1, 'Select at least one team quality'),
  nonNegotiables: z.string().optional(),
  festivalImportance: z.string().min(1, 'Select festival importance'),
  streamerImportance: z.string().min(1, 'Select streamer importance'),
  similarWorkImportance: z.string().min(1, 'Select similar work importance'),
  firstTimeFilmmakerPolicy: z.string().min(1, 'Select first-time filmmaker policy'),
  studioRoles: z.array(z.string()).min(1, 'Select at least one studio role'),
  creativeControlLevel: z.string().min(1, 'Select creative control level'),
  fundingAspects: z.array(z.string()).min(1, 'Select at least one funding aspect'),
  projectReturns: z.array(z.string()).min(1, 'Select at least one project return'),
  additionalExpectations: z.string().optional(),
});

type CriteriaFormData = z.infer<typeof criteriaSchema>;

export function CriteriaForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, setValue } = useForm<CriteriaFormData>({
    resolver: zodResolver(criteriaSchema),
    defaultValues: {
      supportTypes: [],
      distributionGoals: [],
      contentFormats: [],
      teamQualities: [],
      studioRoles: [],
      fundingAspects: [],
      projectReturns: [],
    }
  });

  useEffect(() => {
    const loadExistingCriteria = async () => {
      if (!user) return;

      try {
        // First get the studio
        const { data: studio, error: studioError } = await supabase
          .from('studios')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (studioError) throw studioError;

        // Then get the criteria
        const { data: criteria, error: criteriaError } = await supabase
          .from('studio_criteria')
          .select('*')
          .eq('studio_id', studio.id)
          .single();

        if (criteriaError) throw criteriaError;

        if (studio && criteria) {
          // Set form values from studio data
          setValue('studioName', studio.name);
          setValue('description', studio.description);
          setValue('coreValues', studio.core_values);
          setValue('socialIssues', studio.social_issues);
          setValue('successMetrics', studio.success_metrics);

          // Set form values from criteria data
          setValue('supportTypes', criteria.support_types || []);
          setValue('distributionGoals', criteria.distribution_goals || []);
          setValue('contentFormats', criteria.content_formats || []);
          setValue('teamQualities', criteria.team_qualities || []);
          setValue('nonNegotiables', criteria.non_negotiables);
          setValue('festivalImportance', criteria.festival_importance);
          setValue('streamerImportance', criteria.streamer_importance);
          setValue('similarWorkImportance', criteria.similar_work_importance);
          setValue('firstTimeFilmmakerPolicy', criteria.first_time_filmmaker_policy);
          setValue('studioRoles', criteria.studio_roles || []);
          setValue('creativeControlLevel', criteria.creative_control_level);
          setValue('fundingAspects', criteria.funding_aspects || []);
          setValue('projectReturns', criteria.project_returns || []);
          setValue('additionalExpectations', criteria.additional_expectations);
        }
      } catch (error) {
        console.error('Error loading criteria:', error);
      }
    };

    loadExistingCriteria();
  }, [user, setValue]);

  const onSubmit = async (data: CriteriaFormData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Create studio profile
      const { data: studio, error: studioError } = await supabase
        .from('studios')
        .insert([{
          user_id: user.id,
          name: data.studioName,
          description: data.description,
          core_values: data.coreValues,
          social_issues: data.socialIssues,
          success_metrics: data.successMetrics,
        }])
        .select()
        .single();

      if (studioError) throw studioError;

      // Create studio criteria
      const { error: criteriaError } = await supabase
        .from('studio_criteria')
        .insert([{
          studio_id: studio.id,
          support_types: data.supportTypes,
          distribution_goals: data.distributionGoals,
          content_formats: data.contentFormats,
          team_qualities: data.teamQualities,
          non_negotiables: data.nonNegotiables,
          festival_importance: data.festivalImportance,
          streamer_importance: data.streamerImportance,
          similar_work_importance: data.similarWorkImportance,
          first_time_filmmaker_policy: data.firstTimeFilmmakerPolicy,
          studio_roles: data.studioRoles,
          creative_control_level: data.creativeControlLevel,
          funding_aspects: data.fundingAspects,
          project_returns: data.projectReturns,
          additional_expectations: data.additionalExpectations,
        }]);

      if (criteriaError) throw criteriaError;

      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving criteria:', error);
    }
  };

  const CheckboxGroup = ({ 
    options, 
    name, 
    label, 
    error 
  }: { 
    options: string[], 
    name: string, 
    label: string, 
    error?: string 
  }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        {options.map(option => (
          <label key={option} className="flex items-center space-x-2">
            <input
              type="checkbox"
              value={option}
              {...register(name)}
              className="rounded border-gray-700 bg-gray-800 text-[#F8B500]"
            />
            <span className="text-sm text-gray-300">{option}</span>
          </label>
        ))}
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );

  const RadioGroup = ({ 
    options, 
    name, 
    label, 
    error 
  }: { 
    options: string[], 
    name: string, 
    label: string, 
    error?: string 
  }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="space-y-2">
        {options.map(option => (
          <label key={option} className="flex items-center space-x-2">
            <input
              type="radio"
              value={option}
              {...register(name)}
              className="border-gray-700 bg-gray-800 text-[#F8B500]"
            />
            <span className="text-sm text-gray-300">{option}</span>
          </label>
        ))}
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Studio Criteria</h1>
        <p className="text-gray-400 mt-2">
          Define your studio's requirements and preferences for filmmaker submissions.
        </p>
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="bg-gray-800 rounded-lg p-6 space-y-6">
          <h2 className="text-xl font-semibold text-white mb-4">Basic Information</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="studioName">Studio Name</Label>
              <input
                id="studioName"
                {...register('studioName')}
                className="mt-1 w-full rounded-md border border-gray-700 bg-gray-900 text-white px-4 py-2"
              />
              {errors.studioName && (
                <p className="text-red-500 text-sm mt-1">{errors.studioName.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register('description')}
                className="mt-1 bg-gray-900 border-gray-700 text-white h-32"
                placeholder="Describe your studio's focus and mission"
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 space-y-6">
          <h2 className="text-xl font-semibold text-white mb-4">Values and Focus</h2>
          
          <div>
            <Label htmlFor="coreValues">Core Values</Label>
            <Textarea
              id="coreValues"
              {...register('coreValues')}
              className="mt-1 bg-gray-900 border-gray-700 text-white"
              placeholder="What are your studio's core values?"
            />
            {errors.coreValues && (
              <p className="text-red-500 text-sm mt-1">{errors.coreValues.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="socialIssues">Social Issues</Label>
            <Textarea
              id="socialIssues"
              {...register('socialIssues')}
              className="mt-1 bg-gray-900 border-gray-700 text-white"
              placeholder="What social issues does your studio focus on?"
            />
            {errors.socialIssues && (
              <p className="text-red-500 text-sm mt-1">{errors.socialIssues.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="successMetrics">Success Metrics</Label>
            <Textarea
              id="successMetrics"
              {...register('successMetrics')}
              className="mt-1 bg-gray-900 border-gray-700 text-white"
              placeholder="How do you measure success?"
            />
            {errors.successMetrics && (
              <p className="text-red-500 text-sm mt-1">{errors.successMetrics.message}</p>
            )}
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 space-y-6">
          <h2 className="text-xl font-semibold text-white mb-4">Project Requirements</h2>
          
          <CheckboxGroup
            name="supportTypes"
            label="What types of support do you offer?"
            options={supportTypesOptions}
            error={errors.supportTypes?.message}
          />

          <CheckboxGroup
            name="distributionGoals"
            label="Distribution Goals"
            options={distributionGoalsOptions}
            error={errors.distributionGoals?.message}
          />

          <CheckboxGroup
            name="contentFormats"
            label="Content Formats"
            options={contentFormatsOptions}
            error={errors.contentFormats?.message}
          />

          <CheckboxGroup
            name="teamQualities"
            label="Important Team Qualities"
            options={teamQualitiesOptions}
            error={errors.teamQualities?.message}
          />

          <div>
            <Label htmlFor="nonNegotiables">Non-Negotiables</Label>
            <Textarea
              id="nonNegotiables"
              {...register('nonNegotiables')}
              className="mt-1 bg-gray-900 border-gray-700 text-white"
              placeholder="What are your absolute requirements?"
            />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 space-y-6">
          <h2 className="text-xl font-semibold text-white mb-4">Preferences and Policies</h2>
          
          <RadioGroup
            name="festivalImportance"
            label="How important is festival success?"
            options={festivalImportanceOptions}
            error={errors.festivalImportance?.message}
          />

          <RadioGroup
            name="streamerImportance"
            label="How important are streaming platform relationships?"
            options={streamerImportanceOptions}
            error={errors.streamerImportance?.message}
          />

          <RadioGroup
            name="similarWorkImportance"
            label="How important is previous similar work?"
            options={similarWorkImportanceOptions}
            error={errors.similarWorkImportance?.message}
          />

          <RadioGroup
            name="firstTimeFilmmakerPolicy"
            label="What is your policy on first-time filmmakers?"
            options={firstTimeFilmmakerOptions}
            error={errors.firstTimeFilmmakerPolicy?.message}
          />

          <RadioGroup
            name="creativeControlLevel"
            label="What level of creative control do you expect?"
            options={creativeControlOptions}
            error={errors.creativeControlLevel?.message}
          />
        </div>

        <div className="bg-gray-800 rounded-lg p-6 space-y-6">
          <h2 className="text-xl font-semibold text-white mb-4">Studio Involvement</h2>
          
          <CheckboxGroup
            name="studioRoles"
            label="What roles will your studio play?"
            options={studioRolesOptions}
            error={errors.studioRoles?.message}
          />

          <CheckboxGroup
            name="fundingAspects"
            label="Funding Aspects"
            options={fundingAspectsOptions}
            error={errors.fundingAspects?.message}
          />

          <CheckboxGroup
            name="projectReturns"
            label="Project Returns"
            options={projectReturnsOptions}
            error={errors.projectReturns?.message}
          />

          <div>
            <Label htmlFor="additionalExpectations">Additional Expectations</Label>
            <Textarea
              id="additionalExpectations"
              {...register('additionalExpectations')}
              className="mt-1 bg-gray-900 border-gray-700 text-white"
              placeholder="Any other expectations or requirements?"
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full py-6"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Criteria'}
        </Button>
      </form>
    </div>
  );
}